/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { RestLink } from 'apollo-link-rest';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import _fromPairs from 'lodash/fromPairs';
import { getHeaders } from '@util/auth-token';
import { confirmAPIVersion } from '@util/api-mismatch';
import logException from '@util/js-error';

/**
 * https://caniuse.com/#search=fromEntries
 * Object.fromEntries isn't supported by older browsers, and we're seeing instances
 * where this is causing problems, particularly for iOS 12. This accomplishes the
 * same thing for those browsers. Should be possible to remove this once
 * Cypress supports proper `fetch` observing.
 */
const fromEntriesPolyfill = iter => _fromPairs([...iter]);
const fromEntries = Object.fromEntries || fromEntriesPolyfill;

const restLink = new RestLink({
  endpoints: {
    appraiseAPI: '/appraiseapi2',
    appraiseevalapi: '/appraiseevalapi', // wtf
    awareAPI: '/awareapi2',
    axiomAPI: '/axiomapi',
    crmAPI: '/crmapi',
    curriculumAPI: '/curriculumapi',
    documentsAPI: '/documentsapi',
    eduphoriaWebcontrols: '/eduphoria_webcontrols',
    forethoughtAPI: '/forethoughtapi',
    formManagementAPI: '/form-management',
    managementAPI: '/managementapi',
    itemBankAPI: '/itembankapi',
    frameworkAPI: '/frameworkapi',
    goalsAPI: '/goalsapi',
    legacyAwareAPI: '/aware',
    onlineTestingAPI: '/onlinetestingapi',
    onlineTestingAPI2: '/onlinetestingapi2',
    standardsAPI: '/standardsapi',
    striveAPI: '/striveapi',
    studentPorfoliosAPI: '/studentportfoliosapi',
    testProctoringAPI: '/testproctoringapi',
    workshopAPI: '/workshopapi2'
  },
  uri: '/',
  responseTransformer: async response => {
    try {
      if (!response.json) return Promise.resolve(null);

      confirmAPIVersion(response.headers);

      return response.json().then(payload => ('data' in payload ? payload.data : payload));
    } catch {
      return Promise.resolve(null);
    }
  },
  customFetch: async (url, options) => {
    const headers = await getHeaders(fromEntries(options.headers));
    return fetch(url, {
      ...options,
      headers
    });
  },
  bodySerializers: {
    fileUploader: (file, headers) => {
      const formData = new FormData();
      formData.append('file', file);
      return { body: formData, headers };
    },
    multipart: (data, headers) => {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });

      return { body: formData, headers };
    }
  },
  typePatcher: {
    Evaluation: data => {
      if (data.document) {
        data.document.forEach(group => {
          if (group.groupType === 'Group') {
            group.__typename = 'EvaluationDocumentGroup';
            group.content.forEach(item => {
              item.__typename = 'EvaluationDocumentGroupItem';
            });
          } else {
            group.__typename = 'EvaluationDocumentMatrix';
            group.content.__typename = 'EvaluationDocumentMatrixItem';
          }
        });
      }
      return data;
    },
    SectionWrapper: data => {
      if (data) {
        data.sections.forEach(section => {
          section.students.forEach(student => {
            student.sectionId = section.sectionId;
          });
        });
      }
      return data;
    },
    StudentPortfolio: data => {
      // If data.students is falsy, we've probably just created a new portfolio, so don't do anything
      if (!data.students) {
        return data;
      }

      Object.keys(data.students).forEach(studentGroupType => {
        data.students[studentGroupType].forEach(group => {
          group.__typename = 'StudentGroupInStudentPortfolio';
          group.studentPortfolioId = data.id;
          group.students.forEach(student => {
            student.__typename = 'StudentInStudentPortfolio';
          });
        });
      });

      return data;
    },
    TestEntry: data => {
      if (data.responses) {
        const testEntryId = data.id;
        data.responses.forEach(response => {
          response.testEntryId = testEntryId;
          response.canEnterAnswers = data.canEnterAnswers;
        });
      }
      return data;
    },
    StudentPerceptions: data => {
      if (data) {
        const { trackerId } = data;
        data.perceptionsByStudent.forEach(perceptionObj => {
          perceptionObj.trackerId = trackerId;
        });
      }
      return data;
    },
    OTTest: data => {
      if (data?.enableRandomization) {
        const { randomizeAnswers } = restLink;
        if (randomizeAnswers) {
          return randomizeAnswers(data);
        }
        logException(new Error('Online Testing: failure to randomize answers'));
      }
      return data;
    },
    EntityTree: (
      { parents, children },
      _,
      __,
      {
        resolverParams: {
          args: { documentId }
        }
      }
    ) => {
      // Add documentId to the EntityTree data for proper cache identification
      return { parents, children, documentId };
    }
  }
});

// Configure the ApolloClient with the default cache and RestLink
export default new ApolloClient({
  link: restLink,
  cache: new InMemoryCache({
    possibleTypes: {
      EvaluationDocument: ['EvaluationDocumentGroup', 'EvaluationDocumentMatrix']
    },
    typePolicies: {
      Query: {
        fields: {
          note(existingData, { args, toReference }) {
            return (
              existingData ||
              toReference({
                __typename: 'QuestionNote',
                questionGuid: args.questionGuid,
                testEntryId: args.testEntryId
              })
            );
          },
          notes: {
            merge(_, incoming) {
              return incoming;
            }
          },
          contentClarifier(existingData, { args, toReference }) {
            return (
              existingData ||
              toReference({
                __typename: 'ContentClarifier',
                id: args.id
              })
            );
          },
          studentPortfolioComment(existingData, { args, toReference }) {
            return (
              existingData ||
              toReference({
                __typename: 'StudentPortfolioComment',
                commentId: args.id
              })
            );
          },
          testEntry(existingData, { args, toReference }) {
            return (
              existingData ||
              toReference({
                __typename: 'TestEntry',
                id: args.id
              })
            );
          },
          testResponse(existingData, { args, toReference }) {
            return (
              existingData ||
              toReference({
                __typename: 'TestResponse',
                questionGuid: args.questionGuid,
                testEntryId: args.testEntryId
              })
            );
          },
          // For loading more items with infinite scrolling
          itemBankQuestions(existingData, { args, toReference }) {
            return (
              existingData ||
              toReference({
                __typename: 'ItemBankQuestions',
                params: args.params,
                endpoint: args.endpoint
              })
            );
          },
          enrollments: { merge: false },
          authorAssessments: {
            merge(_, incoming) {
              return incoming;
            }
          },
          sessionAttendees: {
            merge(_, incoming) {
              return incoming;
            }
          },
          dataViews: {
            merge(_, incoming) {
              return incoming;
            }
          },
          messagesByTypeAndParentId: {
            keyArgs: ['messageType', 'parentId']
          },
          paReflectionQuestions: {
            merge(_, incoming) {
              return incoming;
            }
          }
        }
      },
      AchievementLevelsByStudent: {
        keyFields: ['id', 'studentId']
      },
      AchievementLevelStudentDetailsByTest: {
        keyFields: ['id', 'testId', 'studentId']
      },
      AppraiseUser: {
        keyFields: ['appraiseUserId']
      },
      AppraiseeType: {
        keyFields: ['appraiseeTypeId']
      },
      AppraiseeTypeScoringFormula: {
        keyFields: ['appraiseeTypeId']
      },
      BeaconTemplateListItem: {
        keyFields: ['templateGuid']
      },
      QuickViewsDataViewType: {
        keyFields: ['type', 'id']
      },
      Campus: {
        keyFields: ['campusId']
      },
      CurriculumFrameworks: {
        keyFields: ['identifier']
      },
      CurriculumGroupingTypes: {
        keyFields: ['identifier']
      },
      // CRM-only
      Customers: {
        keyFields: ['customerId']
      },
      CustomItemBankImportQuestion: {
        keyFields: ['guid']
      },
      CustomItemBankItem: {
        keyFields: ['guid']
      },
      Entity: {
        keyFields: ['documentId']
      },
      RootEntity: {
        keyFields: ['documentId']
      },
      Evaluation: {
        keyFields: ['evaluationId']
      },
      EntityTree: {
        keyFields: ['documentId']
      },
      EntityTreeItem: {
        keyFields: obj => {
          const parentOrChildGuid = obj.parentDocumentGuid || obj.childDocumentGuid;
          return `${obj.__typename}:${obj.documentId}:${parentOrChildGuid}`;
        }
      },
      EvaluationDocumentGroup: {
        keyFields: ['groupId']
      },
      EvaluationDocumentMatrix: {
        keyFields: ['matrixId']
      },
      Evidence: {
        keyFields: ['evidenceId']
      },
      FrameworkGroupings: {
        keyFields: ['identifier']
      },
      FrameworkStandards: {
        keyFields: ['identifier']
      },
      Goal: {
        keyFields: ['goalId']
      },
      ItemBankDefaultCreateSettings: {
        keyFields: ['courseId']
      },
      ItemBankMutationResponse: {
        keyFields: ['questionId']
      },
      ItemBankQuestion: {
        keyFields: ['guid']
      },
      ItemBankPrebuiltTest: {
        keyFields: ['guid']
      },
      Message: {
        keyFields: ['messageType', 'parentId', 'messageId']
      },
      Milestone: {
        fields: {
          milestoneEntries: {
            merge(_, incoming) {
              return incoming;
            }
          }
        }
      },
      PostAssessmentActivity: {
        keyFields: ['typeId', 'id']
      },
      Question: {
        keyFields: ['guid']
      },
      QuestionNote: {
        keyFields: ['questionGuid', 'testEntryId']
      },
      RosterCourses: {
        keyFields: ['courseId']
      },
      SectionWrapper: {
        keyFields: ['testId']
      },
      Section: {
        keyFields: ['sectionId']
      },
      StriveOverviewWalkthroughCount: {
        keyFields: ['campusId', 'startDate', 'endDate']
      },
      Student: {
        keyFields: ['sectionId', 'studentId']
      },
      StudentFormTemplateTitles: {
        keyFields: ['templateId']
      },
      StudentGroupInStudentPortfolio: {
        keyFields: ['studentPortfolioId', 'id'],
        fields: {
          students: {
            merge(_, incoming) {
              return incoming;
            }
          }
        }
      },
      StudentInStudentPortfolio: {
        keyFields: ['studentId']
      },
      StudentPortfolio: {
        fields: {
          attachedGoalIds: {
            merge(_, incoming) {
              return incoming;
            }
          }
        }
      },
      StudentReflectionStudent: {
        keyFields: ['studentId']
      },
      StudentReflection: {
        keyFields: ['reflectionId']
      },
      StudentReflectionTemplate: {
        keyFields: ['reflectionId']
      },
      StudentReflectionQuestion: {
        keyFields: ['questionId']
      },
      SubmittedStudentReflection: {
        keyFields: ['reflectionId', 'studentId']
      },
      StudentReflectionResponse: {
        keyFields: ['questionId']
      },
      StudentPortfolioComment: {
        keyFields: ['commentId']
      },
      StudentPortalAvailableTest: {
        keyFields: ['testEntryId']
      },
      StudentPortalReflection: {
        id: ['reflectionId']
      },
      StudentPortalScoredTest: {
        keyFields: ['testEntryId']
      },
      StudentPortalStudent: {
        keyFields: ['studentId']
      },
      StudentSummaryType: {
        keyFields: ['testEntryId']
      },
      Template: {
        keyFields: ['templateId']
      },
      Test: {
        keyFields: ['id', 'guid']
      },
      TestResponse: {
        keyFields: ['questionGuid', 'testEntryId'],
        fields: {
          typeProperties: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            }
          }
        }
      },
      ReferenceDocument: {
        keyFields: ['attachmentId', 'testId']
      },
      StudentGrowthScore: {
        keyFields: ['evaluationId']
      },
      StaffStudentGrowthScore: {
        keyFields: ['teacherEmail', 'schoolYearId']
      },
      TrackerStudents: {
        keyFields: ['studentId']
      },
      TrackerTests: {
        keyFields: ['testId']
      },
      Perceptions: {
        keyFields: ['studentId', 'trackerId']
      },
      PerceptionNote: {
        keyFields: ['studentPerceptionId']
      },
      SummaryScore: {
        keyFields: ['trackerId', 'standardId', 'studentId']
      }
    }
  })
});
